import Head from "next/head";
import { GetServerSideProps } from "next";
import { useState } from "react";
import SignupEmbedForm, { SignupEmbedFormInterface } from "~features/embed";
import { SignupEmbedError } from "~features/embed/Error";
import { SignupEmbedSuccess } from "~features/embed/Success";
import { sdk } from "~lib";
import { getServerSideOrganization, setCacheHeaders } from "~lib/server";

interface ErrorProps {
  error: true;
}

function errorProps(): { props: ErrorProps } {
  return { props: { error: true } };
}

export const getServerSideProps: GetServerSideProps<
  SignupEmbedFormInterface | ErrorProps
> = async (context) => {
  const formId = context.query.id;
  const previewProps = context.query.previewProps;

  if (previewProps && typeof previewProps === "string") {
    return {
      // context.query has been automatically decoded
      props: JSON.parse(previewProps),
    };
  }

  if (typeof formId !== "string") {
    return errorProps();
  }

  try {
    const { error, data } = await getServerSideOrganization(context.req);
    const orgId = data?.currentOrganization?.id;

    if (error || !orgId) {
      return errorProps();
    }

    const { signupForm } = await sdk({
      orgId: orgId,
      server: {
        req: context.req,
      },
    }).signupForm({ id: formId });

    if (!signupForm) {
      return errorProps();
    }

    setCacheHeaders(context.res);

    return {
      props: {
        id: signupForm.id,
        heading: signupForm.heading,
        description: signupForm.description,
        buttonText: signupForm.buttonText,
        styles: signupForm.styles,
        formFields: signupForm.formFields,
        tags: signupForm.tags,
        successMessage: signupForm.successMessage,
        successHeading: signupForm.successHeading,
        processedCustomCSS: signupForm.processedCustomCSS,
      },
    };
  } catch (error) {
    return errorProps();
  }
};

function isError(
  props: SignupEmbedFormInterface | ErrorProps
): props is ErrorProps {
  return (props as ErrorProps).error;
}

export default function Embed(props: SignupEmbedFormInterface | ErrorProps) {
  const [modalState, setModalState] = useState<"form" | "success">("form");

  if (isError(props)) {
    return <SignupEmbedError />;
  }

  const { processedCustomCSS } = props;

  return (
    <>
      <Head>
        <style dangerouslySetInnerHTML={{ __html: processedCustomCSS }}></style>
      </Head>
      {modalState === "form" ? (
        <SignupEmbedForm
          key={props.id ?? "test"}
          {...props}
          setModalState={setModalState}
        />
      ) : (
        <SignupEmbedSuccess {...props} />
      )}
    </>
  );
}
